import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {ArrowLeft, Check, PencilLine, Plus, Sparkles, Trash2, X} from "lucide-react";
import {useAuth} from "../../authentication/AuthContext";
import {useProjects} from "../../project/hooks/useProjects";
import {useNoteById} from "../../note/hooks/useNote";
import {usePracticeGenerationOptions} from "../hooks/usePractice";

const KNOWLEDGE_QUESTION_TYPES = [
    {value: 'MULTIPLE_CHOICE', label: 'Multiple Choice'},
    {value: 'TRUE_FALSE', label: 'True / False'},
    {value: 'OPEN_ENDED', label: 'Open Answer'},
];

const CODING_QUESTION_TYPES = [
    {value: 'PREDICT_OUTPUT', label: 'Predict output'},
    {value: 'FILL_THE_BLANK', label: 'Fill the blank'},
    {value: 'FIND_THE_BUG', label: 'Find the bug'},
    {value: 'EXPLAIN_CODE', label: 'Explain code'},
    {value: 'REFACTOR_CODE', label: 'Refactor code'},
    {value: 'IMPLEMENT_FUNCTION', label: 'Implement function'},
    {value: 'REVIEW_A_DIFF', label: 'Review a diff'},
];

const isCodingQuestion = (questionType) => questionType === 'CODE_INPUT' || questionType === 'GUIDED_CODING';

const getCodingType = (item) => (
    item.codingType
    ?? item.coding_type
    ?? (item.questionType === 'GUIDED_CODING' ? 'IMPLEMENT_FUNCTION' : 'PREDICT_OUTPUT')
);

const CODE_LANGUAGES = [
    {value: 'go', label: 'Go'},
    {value: 'javascript', label: 'JavaScript'},
    {value: 'typescript', label: 'TypeScript'},
    {value: 'python', label: 'Python'},
    {value: 'java', label: 'Java'},
    {value: 'kotlin', label: 'Kotlin'},
    {value: 'sql', label: 'SQL'},
];

const createId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createOption = (text = '', isCorrect = false, position = 0) => ({
    id: createId('option'),
    text,
    isCorrect,
    position,
});

const createGuidedStep = (position = 0) => ({
    id: createId('guided-step'),
    position,
    instruction: '',
    validation: '',
});

const createPracticeItem = (questionType = 'MULTIPLE_CHOICE') => {
    if (questionType === 'TRUE_FALSE') {
        return {
            id: createId('item'),
            position: 0,
            questionType: 'TRUE_FALSE',
            prompt: '',
            explanation: '',
            correctAnswer: true,
            allowMultipleAnswers: false,
            options: [],
            expectedAnswer: '',
            language: '',
        };
    }

    if (questionType === 'CODE_INPUT' || questionType === 'GUIDED_CODING') {
        return {
            id: createId('item'),
            position: 0,
            questionType,
            prompt: '',
            explanation: '',
            correctAnswer: null,
            allowMultipleAnswers: false,
            options: [],
            expectedAnswer: '',
            language: 'go',
            codingType: questionType === 'GUIDED_CODING' ? 'IMPLEMENT_FUNCTION' : 'PREDICT_OUTPUT',
            guidedSteps: questionType === 'GUIDED_CODING' ? [createGuidedStep()] : [],
        };
    }

    if (questionType === 'OPEN_ENDED') {
        return {
            id: createId('item'),
            position: 0,
            questionType: 'OPEN_ENDED',
            prompt: '',
            explanation: '',
            correctAnswer: null,
            allowMultipleAnswers: false,
            options: [],
            expectedAnswer: '',
            language: '',
        };
    }

    return {
        id: createId('item'),
        position: 0,
        questionType: 'MULTIPLE_CHOICE',
        prompt: '',
        explanation: '',
        allowMultipleAnswers: false,
        options: [createOption('Option 1', true, 0), createOption('Option 2', false, 1)],
        correctAnswer: null,
        expectedAnswer: '',
        language: '',
        guidedSteps: [],
    };
};

const inputClass = "w-full rounded-lg border border-[#31384C] bg-[#2A2B32] px-3 py-2 text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500";
const labelClass = "text-xs font-medium uppercase tracking-wide text-gray-400";

function ChoiceOptionsEditor({item, onChange}) {
    const options = item.options ?? [];

    const setOptions = (nextOptions) => {
        onChange({
            ...item,
            options: nextOptions.map((option, index) => ({...option, position: index})),
        });
    };

    const updateOption = (optionId, patch) => {
        setOptions(options.map((option) => (option.id === optionId ? {...option, ...patch} : option)));
    };

    const toggleCorrect = (optionId, checked) => {
        if (item.allowMultipleAnswers) {
            updateOption(optionId, {isCorrect: checked});
            return;
        }

        setOptions(options.map((option) => ({
            ...option,
            isCorrect: option.id === optionId ? checked : false,
        })));
    };

    const addOption = () => {
        setOptions([...options, createOption(`Option ${options.length + 1}`, false, options.length)]);
    };

    const removeOption = (optionId) => {
        const nextOptions = options.filter((option) => option.id !== optionId);
        if (nextOptions.length < 2) return;

        const hasCorrect = nextOptions.some((option) => option.isCorrect);
        setOptions((hasCorrect ? nextOptions : nextOptions.map((option, index) => ({
            ...option,
            isCorrect: index === 0,
        }))));
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                        type="checkbox"
                        checked={item.allowMultipleAnswers}
                        onChange={(e) => onChange({...item, allowMultipleAnswers: e.target.checked})}
                        className="h-4 w-4 accent-indigo-500"
                    />
                    Allow multiple correct answers
                </label>
                <button
                    type="button"
                    onClick={addOption}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#3A4257] px-3 py-1.5 text-xs font-medium text-gray-200 transition hover:bg-[#2B3242]"
                >
                    <Plus size={14} />
                    Add option
                </button>
            </div>

            <div className="space-y-2">
                {options.map((option, index) => (
                    <div key={option.id} className="flex items-center gap-2 rounded-lg border border-[#31384C] bg-[#1E1F25] p-2">
                        <input
                            type={item.allowMultipleAnswers ? 'checkbox' : 'radio'}
                            name={`practice-choice-${item.id}`}
                            checked={Boolean(option.isCorrect)}
                            onChange={(e) => toggleCorrect(option.id, e.target.checked)}
                            className="h-4 w-4 accent-indigo-500"
                            aria-label={`Mark option ${index + 1} as correct`}
                        />
                        <input
                            type="text"
                            value={option.text}
                            onChange={(e) => updateOption(option.id, {text: e.target.value})}
                            className="min-w-0 flex-1 rounded-md border border-[#31384C] bg-[#2A2B32] px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder={`Option ${index + 1}`}
                        />
                        <button
                            type="button"
                            onClick={() => removeOption(option.id)}
                            disabled={options.length <= 2}
                            className="rounded-md p-2 text-gray-400 transition hover:bg-[#2A2B32] hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label={`Remove option ${index + 1}`}
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function GuidedStepsEditor({item, onChange}) {
    const steps = item.guidedSteps ?? [];
    const setSteps = (nextSteps) => onChange({
        ...item,
        guidedSteps: nextSteps.map((step, position) => ({...step, position})),
    });

    return (
        <div className="space-y-3">
            {steps.map((step, stepIndex) => (
                <div key={step.id} className="space-y-3 rounded-xl border border-[#31384C] bg-[#25262D] p-4">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-300">Step {stepIndex + 1}</p>
                        <button
                            type="button"
                            onClick={() => setSteps(steps.filter((candidate) => candidate.id !== step.id))}
                            disabled={steps.length === 1}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-[#30313A] hover:text-red-300 disabled:opacity-30"
                            aria-label={`Remove step ${stepIndex + 1}`}
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                    <div className="grid gap-2">
                        <label className={labelClass}>Instruction</label>
                        <textarea
                            value={step.instruction}
                            onChange={(event) => setSteps(steps.map((candidate) => candidate.id === step.id ? {...candidate, instruction: event.target.value} : candidate))}
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="Create a Notifier interface with a Notify method."
                        />
                    </div>
                    <div className="grid gap-2">
                        <label className={labelClass}>Expected / Validation</label>
                        <textarea
                            value={step.validation}
                            onChange={(event) => setSteps(steps.map((candidate) => candidate.id === step.id ? {...candidate, validation: event.target.value} : candidate))}
                            rows={3}
                            className={`${inputClass} font-mono resize-none`}
                            placeholder="type Notifier interface { ... }"
                        />
                    </div>
                </div>
            ))}
            <button
                type="button"
                onClick={() => setSteps([...steps, createGuidedStep(steps.length)])}
                className="inline-flex items-center gap-2 rounded-lg border border-dashed border-indigo-500/50 px-3 py-2 text-sm font-medium text-indigo-300 hover:bg-indigo-500/10"
            >
                <Plus size={15} /> Add step
            </button>
        </div>
    );
}

GuidedStepsEditor.propTypes = {
    item: PropTypes.shape({guidedSteps: PropTypes.array}).isRequired,
    onChange: PropTypes.func.isRequired,
};

ChoiceOptionsEditor.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        allowMultipleAnswers: PropTypes.bool,
        options: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
            isCorrect: PropTypes.bool.isRequired,
        })),
    }).isRequired,
    onChange: PropTypes.func.isRequired,
};

function PracticeItemEditor({item, index, onChange, onRemove, canRemove}) {
    const typeLabel = useMemo(
        () => {
            if (isCodingQuestion(item.questionType)) {
                return `Coding · ${CODING_QUESTION_TYPES.find((type) => type.value === getCodingType(item))?.label ?? 'Coding'}`;
            }

            return KNOWLEDGE_QUESTION_TYPES.find((type) => type.value === item.questionType)?.label ?? item.questionType;
        },
        [item]
    );

    return (
        <div className="rounded-2xl border border-[#31384C] bg-[#1E1F25] p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Question {index + 1}</p>
                    <p className="text-sm font-semibold text-gray-100">{typeLabel}</p>
                </div>
                {canRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#3A4257] px-3 py-1.5 text-xs font-medium text-gray-200 transition hover:bg-[#2B3242]"
                    >
                        <Trash2 size={14} />
                        Remove
                    </button>
                )}
            </div>

            <div className="grid gap-4">
                <div className="grid gap-2">
                    <label className={labelClass}>Question type</label>
                    <select
                        value={isCodingQuestion(item.questionType) ? 'CODING' : item.questionType}
                        onChange={(e) => {
                            const nextType = e.target.value;
                            const nextItem = createPracticeItem(nextType === 'CODING' ? 'CODE_INPUT' : nextType);
                            onChange({
                                ...nextItem,
                                id: item.id,
                                position: item.position,
                                prompt: item.prompt,
                                explanation: item.explanation,
                                language: item.language || nextItem.language,
                            });
                        }}
                        className={inputClass}
                    >
                        <optgroup label="Knowledge">
                            {KNOWLEDGE_QUESTION_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </optgroup>
                        <optgroup label="Coding">
                            <option value="CODING">Coding</option>
                        </optgroup>
                    </select>
                </div>

                {isCodingQuestion(item.questionType) && (
                    <div className="grid gap-2">
                        <label className={labelClass}>Coding type</label>
                        <select
                            value={getCodingType(item)}
                            onChange={(e) => onChange({
                                ...item,
                                questionType: 'CODE_INPUT',
                                codingType: e.target.value,
                                guidedSteps: [],
                            })}
                            className={inputClass}
                        >
                            {CODING_QUESTION_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="grid gap-2">
                    <label className={labelClass}>{item.questionType === 'GUIDED_CODING' ? 'Title' : 'Prompt'}</label>
                    <textarea
                        value={item.prompt}
                        onChange={(e) => onChange({...item, prompt: e.target.value})}
                        rows={3}
                        className={`${inputClass} resize-none`}
                        placeholder={item.questionType === 'GUIDED_CODING' ? 'Build a notification service' : 'Ask the question here'}
                    />
                </div>

                {item.questionType === 'MULTIPLE_CHOICE' && (
                    <ChoiceOptionsEditor item={item} onChange={onChange} />
                )}

                {item.questionType === 'TRUE_FALSE' && (
                    <div className="grid gap-2">
                        <label className={labelClass}>Correct answer</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => onChange({...item, correctAnswer: true})}
                                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                    item.correctAnswer === true
                                        ? 'border-indigo-500 bg-indigo-500/15 text-indigo-300'
                                        : 'border-[#31384C] bg-[#2A2B32] text-gray-300 hover:bg-[#30384D]'
                                }`}
                            >
                                True
                            </button>
                            <button
                                type="button"
                                onClick={() => onChange({...item, correctAnswer: false})}
                                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                    item.correctAnswer === false
                                        ? 'border-indigo-500 bg-indigo-500/15 text-indigo-300'
                                        : 'border-[#31384C] bg-[#2A2B32] text-gray-300 hover:bg-[#30384D]'
                                }`}
                            >
                                False
                            </button>
                        </div>
                    </div>
                )}

                {item.questionType === 'CODE_INPUT' && (
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <label className={labelClass}>Language</label>
                            <select
                                value={item.language}
                                onChange={(e) => onChange({...item, language: e.target.value})}
                                className={inputClass}
                            >
                                {CODE_LANGUAGES.map((language) => (
                                    <option key={language.value} value={language.value}>{language.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid gap-2">
                            <label className={labelClass}>Expected answer</label>
                            <textarea
                                value={item.expectedAnswer}
                                onChange={(e) => onChange({...item, expectedAnswer: e.target.value})}
                                rows={4}
                                className={`${inputClass} font-mono resize-none`}
                                placeholder="ch := make(chan string, 10)"
                            />
                        </div>
                    </div>
                )}

                {item.questionType === 'GUIDED_CODING' && (
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <label className={labelClass}>Language</label>
                            <select value={item.language} onChange={(e) => onChange({...item, language: e.target.value})} className={inputClass}>
                                {CODE_LANGUAGES.map((language) => <option key={language.value} value={language.value}>{language.label}</option>)}
                            </select>
                        </div>
                        <GuidedStepsEditor item={item} onChange={onChange} />
                    </div>
                )}

                {item.questionType === 'OPEN_ENDED' && (
                    <div className="grid gap-2">
                        <label className={labelClass}>Expected answer</label>
                        <textarea
                            value={item.expectedAnswer}
                            onChange={(e) => onChange({...item, expectedAnswer: e.target.value})}
                            rows={4}
                            className={`${inputClass} resize-none`}
                            placeholder="Write the expected answer"
                        />
                    </div>
                )}

                <div className="grid gap-2">
                    <label className={labelClass}>Explanation</label>
                    <textarea
                        value={item.explanation}
                        onChange={(e) => onChange({...item, explanation: e.target.value})}
                        rows={2}
                        className={`${inputClass} resize-none`}
                        placeholder="Optional explanation"
                    />
                </div>
            </div>
        </div>
    );
}

PracticeItemEditor.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        position: PropTypes.number,
        questionType: PropTypes.string.isRequired,
        prompt: PropTypes.string,
        explanation: PropTypes.string,
        allowMultipleAnswers: PropTypes.bool,
        options: PropTypes.array,
        correctAnswer: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        expectedAnswer: PropTypes.string,
        language: PropTypes.string,
        guidedSteps: PropTypes.array,
        codingType: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    canRemove: PropTypes.bool.isRequired,
};

export default function AddPracticeSetModal({onClose, onSubmit, onGenerate, isSubmitting = false, isGenerating = false, initialPracticeSet = null}) {
    const {authToken} = useAuth();
    const isEditing = Boolean(initialPracticeSet?.id);
    const sourceNoteId = initialPracticeSet?.noteId ?? null;
    const isNoteGenerated = isEditing && Boolean(sourceNoteId);
    const {data: projects = [], isLoading: areProjectsLoading} = useProjects(authToken);
    const {data: sourceNote, isLoading: isSourceNoteLoading} = useNoteById(authToken, sourceNoteId);
    const [creationMode, setCreationMode] = useState(() => isEditing ? 'manual' : null);
    const {data: generationOptions, isLoading: areGenerationOptionsLoading, error: generationOptionsError} = usePracticeGenerationOptions(authToken, creationMode === 'ai');
    const [aiForm, setAiForm] = useState({
        category: '',
        topic: '',
        language: '',
        practiceType: '',
        difficulty: 'ADVANCED',
        questionCount: 5,
        focus: '',
    });
    const [form, setForm] = useState(() => ({
        title: initialPracticeSet?.title ?? '',
        description: initialPracticeSet?.description ?? '',
        projectId: initialPracticeSet?.projectId ?? '',
        noteId: initialPracticeSet?.noteId ?? '',
        language: initialPracticeSet?.language ?? '',
        practiceItems: initialPracticeSet?.practiceItems?.length
            ? initialPracticeSet.practiceItems
            : [createPracticeItem('MULTIPLE_CHOICE')],
    }));

    useEffect(() => {
        setCreationMode(initialPracticeSet?.id ? 'manual' : null);
        setForm({
            title: initialPracticeSet?.title ?? '',
            description: initialPracticeSet?.description ?? '',
            projectId: initialPracticeSet?.projectId ?? '',
            noteId: initialPracticeSet?.noteId ?? '',
            language: initialPracticeSet?.language ?? '',
            practiceItems: initialPracticeSet?.practiceItems?.length
                ? initialPracticeSet.practiceItems
                : [createPracticeItem('MULTIPLE_CHOICE')],
        });
    }, [initialPracticeSet]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const updateItem = (index, nextItem) => {
        setForm((prev) => ({
            ...prev,
            practiceItems: prev.practiceItems.map((item, itemIndex) => (
                itemIndex === index ? {...nextItem, position: index} : {...item, position: itemIndex}
            )),
        }));
    };

    const addItem = () => {
        setForm((prev) => ({
            ...prev,
            practiceItems: [...prev.practiceItems, {...createPracticeItem('MULTIPLE_CHOICE'), position: prev.practiceItems.length}],
        }));
    };

    const removeItem = (index) => {
        setForm((prev) => ({
            ...prev,
            practiceItems: prev.practiceItems
                .filter((_, itemIndex) => itemIndex !== index)
                .map((item, itemIndex) => ({...item, position: itemIndex})),
        }));
    };

    const handleSubmit = () => {
        onSubmit({
            title: form.title.trim(),
            description: form.description.trim(),
            projectId: form.projectId || null,
            noteId: isNoteGenerated ? sourceNoteId : null,
            language: form.language || null,
            practiceItems: form.practiceItems,
        });
    };

    const selectCategory = (category) => {
        setAiForm((current) => ({
            ...current,
            category,
            topic: '',
            language: '',
            practiceType: '',
        }));
    };

    const categories = generationOptions?.categories ?? [];
    const selectedCategory = categories.find((category) => category.id === aiForm.category);
    const isComputerScience = aiForm.category === 'CS';
    const isLanguageLearning = aiForm.category === 'LANGUAGES';
    const usesLanguageAndTypeFlow = isComputerScience || isLanguageLearning;
    const hasCompletedAiPath = usesLanguageAndTypeFlow
        ? Boolean(aiForm.language && aiForm.practiceType)
        : Boolean(aiForm.category && aiForm.topic);

    const handleGenerate = () => {
        onGenerate?.({
            category: aiForm.category,
            topic: usesLanguageAndTypeFlow ? null : aiForm.topic,
            language: usesLanguageAndTypeFlow ? aiForm.language : null,
            practice_type: usesLanguageAndTypeFlow ? aiForm.practiceType : null,
            difficulty: aiForm.difficulty,
            question_count: Number(aiForm.questionCount),
            focus: aiForm.focus.trim() || null,
        });
    };

    const isBusy = isSubmitting || isGenerating;
    const canGenerate = hasCompletedAiPath
        && Number(aiForm.questionCount) >= 1
        && Number(aiForm.questionCount) <= 20;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 pb-4 pt-[92px] backdrop-blur-sm" onClick={handleBackdropClick}>
            <div className={`relative flex w-full ${creationMode === 'manual' ? 'max-w-4xl' : 'max-w-xl'} max-h-[calc(100vh-108px)] flex-col rounded-2xl border border-[#2A2B32] bg-[#1E1F25] p-6 shadow-xl`}>
                <button type="button" onClick={onClose} disabled={isBusy} className="absolute right-4 top-4 rounded-lg p-1.5 text-gray-400 transition hover:bg-[#2A2B32] hover:text-gray-200 disabled:cursor-not-allowed disabled:opacity-60" aria-label="Close practice modal">
                    <X size={18} />
                </button>

                <div className="mb-5 pr-8">
                    {creationMode && !isEditing && (
                        <button type="button" onClick={() => setCreationMode(null)} disabled={isBusy} className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-gray-400 transition hover:text-gray-200 disabled:opacity-50">
                            <ArrowLeft size={14} /> Back
                        </button>
                    )}
                    <h2 className="text-xl font-semibold text-gray-100">
                        {isEditing ? 'Update Practice' : creationMode === 'ai' ? 'Generate Practice' : creationMode === 'manual' ? 'Create Manually' : 'Create Practice'}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        {creationMode === 'ai' ? 'Choose a few options and let AI build the questions.' : creationMode === 'manual' ? 'Build each question yourself.' : 'How would you like to create it?'}
                    </p>
                </div>

                {creationMode === null && (
                    <div className="grid gap-3 sm:grid-cols-2">
                        <button type="button" onClick={() => setCreationMode('ai')} className="group rounded-2xl border border-indigo-500/35 bg-indigo-500/10 p-5 text-left transition hover:border-indigo-400 hover:bg-indigo-500/15">
                            <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/20 text-indigo-300"><Sparkles size={20} /></span>
                            <span className="block text-base font-semibold text-gray-100">Generate with AI</span>
                            <span className="mt-1 block text-sm leading-5 text-gray-400">Describe what you want to practise</span>
                        </button>
                        <button type="button" onClick={() => setCreationMode('manual')} className="group rounded-2xl border border-[#31384C] bg-[#25262D] p-5 text-left transition hover:border-gray-500 hover:bg-[#2A2B32]">
                            <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-gray-300"><PencilLine size={20} /></span>
                            <span className="block text-base font-semibold text-gray-100">Create manually</span>
                            <span className="mt-1 block text-sm leading-5 text-gray-400">Build each question yourself</span>
                        </button>
                    </div>
                )}

                {creationMode === 'ai' && (
                    <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
                        {areGenerationOptionsLoading && (
                            <div className="grid grid-cols-2 gap-2" aria-label="Loading topics">
                                {new Array(5).fill(null).map((_, index) => <div key={index} className="h-14 animate-pulse rounded-xl bg-[#25262D]" />)}
                            </div>
                        )}
                        {generationOptionsError && (
                            <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                                Practice topics could not be loaded. Please try again.
                            </p>
                        )}
                        {!areGenerationOptionsLoading && !generationOptionsError && (
                            <fieldset className="grid gap-2">
                                <legend className={labelClass}>Choose a topic</legend>
                                <div className="grid grid-cols-2 gap-2">
                                    {categories.map((category) => (
                                        <button key={category.id} type="button" aria-pressed={aiForm.category === category.id} onClick={() => selectCategory(category.id)} className={`rounded-xl border px-4 py-3 text-left text-sm font-medium transition ${aiForm.category === category.id ? 'border-indigo-400 bg-indigo-500/15 text-indigo-200' : 'border-[#31384C] bg-[#25262D] text-gray-300 hover:bg-[#2A2B32]'}`}>
                                            {category.label}
                                        </button>
                                    ))}
                                </div>
                            </fieldset>
                        )}

                        {selectedCategory && !usesLanguageAndTypeFlow && (
                            <fieldset className="grid gap-2">
                                <legend className={labelClass}>Choose an area</legend>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {(selectedCategory.topics ?? []).map((topic) => (
                                        <button key={topic.id} type="button" aria-pressed={aiForm.topic === topic.id} onClick={() => setAiForm((current) => ({...current, topic: topic.id}))} className={`rounded-lg border px-3 py-2.5 text-left text-sm transition ${aiForm.topic === topic.id ? 'border-indigo-400 bg-indigo-500/15 text-indigo-200' : 'border-[#31384C] bg-[#25262D] text-gray-300 hover:bg-[#2A2B32]'}`}>
                                            {topic.label}
                                        </button>
                                    ))}
                                </div>
                            </fieldset>
                        )}

                        {usesLanguageAndTypeFlow && (
                            <div className="grid gap-2">
                                <label className={labelClass} htmlFor="practice-language">Language</label>
                                <select id="practice-language" value={aiForm.language} onChange={(e) => setAiForm((current) => ({...current, language: e.target.value, practiceType: ''}))} className={inputClass}>
                                    <option value="">Choose a language</option>
                                    {(selectedCategory?.languages ?? []).map((language) => <option key={language.id} value={language.id}>{language.label}</option>)}
                                </select>
                            </div>
                        )}

                        {usesLanguageAndTypeFlow && aiForm.language && (
                            <fieldset className="grid gap-2">
                                <legend className={labelClass}>Choose a practice type</legend>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {(selectedCategory?.practice_types ?? []).map((type) => (
                                        <button key={type.id} type="button" aria-pressed={aiForm.practiceType === type.id} onClick={() => setAiForm((current) => ({...current, practiceType: type.id}))} className={`rounded-lg border px-3 py-2.5 text-left text-sm transition ${aiForm.practiceType === type.id ? 'border-indigo-400 bg-indigo-500/15 text-indigo-200' : 'border-[#31384C] bg-[#25262D] text-gray-300 hover:bg-[#2A2B32]'}`}>
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                            </fieldset>
                        )}

                        {hasCompletedAiPath && <>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2"><label className={labelClass}>Difficulty</label><select value={aiForm.difficulty} onChange={(e) => setAiForm((current) => ({...current, difficulty: e.target.value}))} className={inputClass}><option value="BEGINNER">Beginner</option><option value="INTERMEDIATE">Intermediate</option><option value="ADVANCED">Advanced</option></select></div>
                            <div className="grid gap-2"><label className={labelClass}>Questions</label><input type="number" min="1" max="20" value={aiForm.questionCount} onChange={(e) => setAiForm((current) => ({...current, questionCount: e.target.value}))} className={inputClass} /></div>
                        </div>
                        <div className="grid gap-2"><label className={labelClass}>Focus <span className="normal-case tracking-normal text-gray-600">— optional</span></label><input type="text" value={aiForm.focus} onChange={(e) => setAiForm((current) => ({...current, focus: e.target.value}))} className={inputClass} placeholder="Channels, goroutine leaks and cancellation" /></div>
                        <div className="flex justify-end border-t border-[#2A2B32] pt-4">
                            <button type="button" onClick={handleGenerate} disabled={isGenerating || !canGenerate} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"><Sparkles size={16} />{isGenerating ? 'Generating...' : 'Generate Practice'}</button>
                        </div>
                        </>}
                    </div>
                )}

                {creationMode === 'manual' && <>
                <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <label className={labelClass}>Title</label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(e) => setForm((prev) => ({...prev, title: e.target.value}))}
                                className={inputClass}
                                placeholder="Practice title"
                            />
                        </div>
                    </div>

                    {isNoteGenerated ? (
                        <div className="grid gap-2">
                            <label className={labelClass}>Source note</label>
                            <div className="rounded-lg border border-[#31384C] bg-[#25262D] px-3 py-2 text-sm text-gray-300">
                                {isSourceNoteLoading ? 'Loading note...' : (sourceNote?.title || sourceNoteId)}
                            </div>
                            <p className="text-xs text-gray-500">This practice was generated from a note. Its source cannot be changed.</p>
                        </div>
                    ) : (
                        <div className="grid gap-2">
                            <label className={labelClass}>Project</label>
                            <select
                                value={form.projectId}
                                onChange={(e) => setForm((prev) => ({...prev, projectId: e.target.value}))}
                                disabled={areProjectsLoading}
                                className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                            >
                                <option value="">{areProjectsLoading ? 'Loading projects...' : 'No project'}</option>
                                {projects.map((project) => {
                                    const projectId = project.id ?? project.projectId;
                                    return (
                                        <option key={projectId} value={projectId}>
                                            {project.name || 'Untitled Project'}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}

                    <div className="grid gap-2">
                        <label className={labelClass}>Description</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm((prev) => ({...prev, description: e.target.value}))}
                            rows={3}
                            className={`${inputClass} resize-none`}
                            placeholder="Optional description"
                        />
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-100">Questions</h3>
                                <p className="text-xs text-gray-500">Add one or more questions to this practice set.</p>
                            </div>
                            <button
                                type="button"
                                onClick={addItem}
                                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-500"
                            >
                                <Plus size={16} />
                                Add question
                            </button>
                        </div>

                        <div className="space-y-4">
                            {form.practiceItems.map((item, index) => (
                                <PracticeItemEditor
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    canRemove={form.practiceItems.length > 1}
                                    onRemove={() => removeItem(index)}
                                    onChange={(nextItem) => updateItem(index, nextItem)}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t border-[#2A2B32] pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="rounded-lg border border-[#3A4257] px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-[#2A2B32] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !form.title.trim() || form.practiceItems.length === 0}
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <Check size={16} />
                        {isSubmitting ? 'Saving...' : (isEditing ? 'Update Practice' : 'Save Practice')}
                    </button>
                </div>
                </>}
            </div>
        </div>
    );
}

AddPracticeSetModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onGenerate: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isGenerating: PropTypes.bool,
    initialPracticeSet: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        projectId: PropTypes.string,
        noteId: PropTypes.string,
        language: PropTypes.string,
        practiceItems: PropTypes.array,
    }),
};
